import { useSortable } from '@dnd-kit/sortable';
import { Box, Stack, Typography } from '@mui/material';

import { AlertTriangleFilledIcon } from '@/components/icons/AlertTriangleFilledIcon';
import { Draghandle } from '@/components/lists/DragAndDropList/Draghandle';
import { getSortableItemStyle } from '@/components/lists/DragAndDropList/getSortableItemStyle';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { DraggableListItemRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DraggableListItemRecipient';
import { PrimaryClientDeathEvent } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { COLORS } from '@/styles/tokens/colors';

interface PrimaryClientDeathSectionRecipientProps {
  recipientId: string;
  index: number;
  deathEvent: PrimaryClientDeathEvent;
  onRemove: () => void;
  forceExpandIds: string[];
  defaultExpandAll: boolean;
  totalRecipientsCount: number;
  needsWarning: boolean;
  isFirstRecipientWithWarning: boolean;
  /** Whether this asset DP recipient appears below entity-level DPs */
  needsOrderingWarning: boolean;
  /** Whether this is the first recipient with the ordering warning */
  isFirstRecipientWithOrderingWarning: boolean;
  disabled?: boolean;
}

export function PrimaryClientDeathSectionRecipient({
  recipientId,
  index,
  deathEvent,
  onRemove,
  forceExpandIds,
  defaultExpandAll,
  totalRecipientsCount,
  needsWarning,
  isFirstRecipientWithWarning,
  needsOrderingWarning,
  isFirstRecipientWithOrderingWarning,
  disabled = false,
}: PrimaryClientDeathSectionRecipientProps) {
  const canEditHouseholdData = useCanEditDispositiveProvisions();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: recipientId,
    disabled,
  });

  const style = getSortableItemStyle({ transform, transition, isDragging });

  const isFirstRecipient = index === 0;
  const isLastRecipient = index === totalRecipientsCount - 1;

  const hasAnyWarning = needsWarning || needsOrderingWarning;
  const isFirstWithAnyWarning =
    isFirstRecipientWithWarning || isFirstRecipientWithOrderingWarning;

  /**
   * Compute top padding for the recipient list item.
   * - First item with a warning banner: extra space (2) for the banner text
   * - First recipient overall: no top padding
   * - Subsequent items inside the warning container: 0 (gap is handled by
   *   the previous item's bottom padding, yielding an 8px inter-card gap)
   * - Normal (non-warning) items: 1 (8px)
   */
  function getTopPadding(): number {
    if (isFirstWithAnyWarning) return 2;
    if (isFirstRecipient) return 0;
    if (hasAnyWarning) return 0;
    return 1;
  }

  return (
    <Box
      component="li"
      ref={setNodeRef}
      style={style}
      // Preserve data attribute for scroll-into-view functionality in parent component
      data-rbd-draggable-id={recipientId}
      sx={{
        listStyleType: 'none',
        p: 0,
        pt: getTopPadding(),
        pb: 0,
        m: 0,
        mt: isFirstRecipient ? 0 : isFirstWithAnyWarning ? 1 : 0,
        borderTopLeftRadius: isFirstWithAnyWarning ? 4 : 0,
        borderTopRightRadius: isFirstWithAnyWarning ? 4 : 0,
        borderBottomLeftRadius: isLastRecipient ? 4 : 0,
        borderBottomRightRadius: isFirstWithAnyWarning ? 4 : 0,
        ...(hasAnyWarning && {
          backgroundColor: COLORS.FUNCTIONAL.WARNING[100],
          px: 2,
          pb: isLastRecipient ? 2 : 1,
        }),
      }}
    >
      {isFirstRecipientWithWarning && (
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <AlertTriangleFilledIcon
            size={16}
            color={COLORS.FUNCTIONAL.WARNING[700]}
          />
          <Typography
            variant="title-xs"
            sx={{
              color: COLORS.FUNCTIONAL.WARNING[700],
              mb: 1,
            }}
          >
            These recipients will not receive dispositions; the selected entity
            fully distributes above
          </Typography>
        </Stack>
      )}
      {isFirstRecipientWithOrderingWarning && (
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <AlertTriangleFilledIcon
            size={16}
            color={COLORS.FUNCTIONAL.WARNING[700]}
          />
          <Typography
            variant="title-xs"
            sx={{
              color: COLORS.FUNCTIONAL.WARNING[700],
              mb: 1,
            }}
          >
            These recipients will not receive dispositions; move asset specific
            dispositions above all others
          </Typography>
        </Stack>
      )}
      <DraggableListItemRecipient
        key={recipientId}
        deathEvent={deathEvent}
        onRemove={canEditHouseholdData ? onRemove : undefined}
        defaultExpanded={
          forceExpandIds.includes(recipientId) || defaultExpandAll
        }
        accordionId={recipientId}
        index={index}
        Draghandle={
          canEditHouseholdData ? (
            <Draghandle {...listeners} {...attributes} />
          ) : null
        }
      />
    </Box>
  );
}
